import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { getSectionLog } from '../../features/leaderboard/leaderboardSlice';
import BSCircularProgress from '../ui/progress/circularProgress';
import Input from '../ui/forms/fields/input';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { splitSection } from '../../features/event/eventSlice';

const StyledSectionTotalTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'showBorder'
})(({ theme, showBorder }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.blue.main,
    color: theme.palette.common.white,
    borderBottom: showBorder ? '2px solid #fff' : 'none'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    borderBottom: showBorder ? '2px solid ' : 'none',
    borderColor: theme.palette.blue.main
  }
}));

const schema = yup.object().shape({
  splitMode: yup.string().oneOf(['position', 'sections']).required('Split mode is required'),
  splitAt: yup.number().when('splitMode', {
    is: 'position',
    then: (schema) =>
      schema.min(1, 'Split position must be at least 1').required('Split position is required'),
    otherwise: (schema) => schema
  }),
  numSections: yup.number().when('splitMode', {
    is: 'sections',
    then: (schema) =>
      schema
        .min(2, 'Must split into at least 2 sections')
        .required('Number of sections is required'),
    otherwise: (schema) => schema
  }),
  resetPoints: yup.boolean(),
  keepNetScores: yup.boolean(),
  dividePointsBy: yup.number().when('resetPoints', {
    is: (resetPoints) => resetPoints === false,
    then: (schema) =>
      schema
        .min(1, 'Divide points by must be at least 1')
        .positive('Must be a positive number')
        .typeError('Must be a number')
        .required('Divide points by is required'),
    otherwise: (schema) => schema.typeError('Must be a number')
  })
});

const SplitSectionDialog = ({ event, section, open, onClose }) => {
  const [newLogs, setNewLogs] = useState([]);
  const [loadingNewLog, setLoadingNewLog] = useState(false);
  const dispatch = useDispatch();

  const sectionLog = useSelector((state) => state.leaderboard.sectionLog);
  const loadingSectionLog = useSelector((state) => state.leaderboard.loadingSectionLog);
  const splittingSection = useSelector((state) => state.event.splittingSection);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      splitMode: 'sections',
      splitAt: 1,
      numSections: 2,
      resetPoints: false,
      keepNetScores: false
    }
  });

  const watchDividePointsBy = watch('dividePointsBy');
  const watchSplitMode = watch('splitMode');
  const watchNumSections = watch('numSections');
  const watchSplitAt = watch('splitAt');
  const watchResetPoints = watch('resetPoints');
  const watchKeepNetScores = watch('keepNetScores');

  useEffect(() => {
    if (watchResetPoints) {
      setValue('dividePointsBy', undefined);
    }
  }, [watchResetPoints, setValue]);

  useEffect(() => {
    if (open && !sectionLog) {
      handleSectionLog();
    }
  }, [open]);

  useEffect(() => {
    if (sectionLog && sectionLog.length > 0) {
      setLoadingNewLog(true);

      if (watchSplitMode === 'sections' && watchNumSections) {
        // Split into N equal sections
        const totalTeams = sectionLog.length;
        const baseTeamsPerSection = Math.floor(totalTeams / watchNumSections);
        const remainder = totalTeams % watchNumSections;
        const splits = [];

        let currentIndex = baseTeamsPerSection + (remainder > 0 ? 1 : 0);
        for (let i = 1; i < watchNumSections; i++) {
          const nextIndex = currentIndex + baseTeamsPerSection + (remainder > i ? 1 : 0);
          splits.push(sectionLog.slice(currentIndex, nextIndex));
          currentIndex = nextIndex;
        }

        setNewLogs(splits);
      } else if (watchSplitMode === 'position' && watchSplitAt) {
        // Split at specific position
        setNewLogs([sectionLog.slice(watchSplitAt)]);
      }

      setLoadingNewLog(false);
    }
  }, [sectionLog, watchSplitMode, watchNumSections, watchSplitAt]);

  // const handleOpen = () => {
  //   setOpen(true);
  //   handleSectionLog();
  // };

  // const handleClose = () => {
  //   setOpen(false);
  // };

  const handleSectionLog = () => {
    dispatch(getSectionLog({ eventId: event.id, sectionId: section.id }));
  };

  const handleSplit = (data) => {
    data.eventId = event.id;
    data.sectionId = section.id;

    dispatch(splitSection(data))
      .unwrap()
      .then(() => {
        onClose();
      })
      .catch((error) => {
        console.error('Error splitting section:', error);
      });
  };

  const mainSection = () => {
    let firstSectionTeams = sectionLog || [];

    if (watchSplitMode === 'sections' && watchNumSections) {
      const teamsPerSection = Math.ceil((sectionLog?.length || 0) / watchNumSections);
      firstSectionTeams = sectionLog?.slice(0, teamsPerSection) || [];
    } else if (watchSplitMode === 'position' && watchSplitAt) {
      firstSectionTeams = sectionLog?.slice(0, watchSplitAt) || [];
    }

    return (
      <TableContainer
        key={section.id}
        component={Paper}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'blue.main'
        }}>
        <Table padding="none">
          <TableHead>
            <TableRow>
              <StyledSectionTotalTableCell padding="normal">
                <Typography variant="body1" fontWeight={'bold'}>
                  {section.name}
                </Typography>
              </StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal">Team</StyledSectionTotalTableCell>
              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                <Tooltip title="Total Points">
                  <Typography variant="p" fontWeight={'bold'} color="white">
                    Pts
                  </Typography>
                </Tooltip>
              </StyledSectionTotalTableCell>

              <StyledSectionTotalTableCell padding="normal" align={'center'}>
                <Tooltip title="Total Net Points">
                  <Typography color="white">Net</Typography>
                </Tooltip>
              </StyledSectionTotalTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {firstSectionTeams.map((log, index) => (
              <TableRow key={log.teamId}>
                <StyledSectionTotalTableCell size="small" padding="normal">
                  {index + 1}
                </StyledSectionTotalTableCell>
                <StyledSectionTotalTableCell size="small" padding="normal">
                  {log.teamName}
                </StyledSectionTotalTableCell>
                <StyledSectionTotalTableCell
                  size="small"
                  align={'center'}
                  sx={{ backgroundColor: 'blue.main' }}>
                  <Typography variant="p" fontWeight={'bold'} color="white">
                    {log.points}
                  </Typography>
                </StyledSectionTotalTableCell>
                <StyledSectionTotalTableCell size="small" padding="normal">
                  {log.net}
                </StyledSectionTotalTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>Split Section</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" mb={2} gap={2}>
            <Box>
              <Typography variant="body2" mb={1} fontWeight="bold">
                Split Mode
              </Typography>
              <Select value={watchSplitMode} {...register('splitMode')} fullWidth size="small">
                <MenuItem value="sections">Split into N equal sections</MenuItem>
                <MenuItem value="position">Split at position</MenuItem>
              </Select>
            </Box>

            {watchSplitMode === 'sections' && (
              <Input
                label={'Number of Sections'}
                error={errors.numSections}
                helperText={errors.numSections?.message}
                type="number"
                {...register('numSections')}
              />
            )}

            {watchSplitMode === 'position' && (
              <Input
                label={'Split after position'}
                error={errors.splitAt}
                helperText={errors.splitAt?.message}
                type="number"
                {...register('splitAt')}
              />
            )}

            <FormControlLabel
              control={<Checkbox {...register('resetPoints')} />}
              label="Reset points to 0 for new sections"
            />
            <FormControlLabel
              control={<Checkbox {...register('keepNetScores')} />}
              label="Keep net scores in new sections"
            />
            {!watchResetPoints && (
              <Input
                label={'Divide Points By'}
                error={errors.dividePointsBy}
                helperText={errors.dividePointsBy?.message}
                type="number"
                disabled={watchResetPoints}
                {...register('dividePointsBy')}
              />
            )}
          </Box>

          {loadingSectionLog ? (
            <BSCircularProgress
              caption="Loading section log..."
              size={24}
              thickness={4}
              color="primary"
            />
          ) : (
            <Box
              display="flex"
              flexDirection="row"
              justifyContent={'center'}
              p={2}
              gap={2}
              flexWrap="wrap">
              <Box display="flex" flexDirection="column" mr={2}>
                {mainSection()}
              </Box>
              {loadingNewLog ? (
                <BSCircularProgress
                  caption="Calculating new sections..."
                  size={24}
                  thickness={4}
                  color="primary"
                />
              ) : (
                newLogs.map((newLog, sectionIndex) => (
                  <TableContainer
                    key={sectionIndex}
                    component={Paper}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'blue.main'
                    }}>
                    <Table padding="none">
                      <TableHead>
                        <TableRow>
                          <StyledSectionTotalTableCell padding="normal">
                            <Typography variant="body1" fontWeight={'bold'}>
                              {watchSplitMode === 'sections'
                                ? `New Section ${sectionIndex + 2}`
                                : 'New Section'}
                            </Typography>
                          </StyledSectionTotalTableCell>
                          <StyledSectionTotalTableCell padding="normal">
                            Team
                          </StyledSectionTotalTableCell>
                          <StyledSectionTotalTableCell padding="normal" align={'center'}>
                            <Tooltip title="Total Points">
                              <Typography variant="p" fontWeight={'bold'} color="white">
                                Pts
                              </Typography>
                            </Tooltip>
                          </StyledSectionTotalTableCell>
                          <StyledSectionTotalTableCell padding="normal" align={'center'}>
                            <Tooltip title="Total Net Points">
                              <Typography color="white">Net</Typography>
                            </Tooltip>
                          </StyledSectionTotalTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {newLog.map((log, index) => (
                          <TableRow key={log.teamId}>
                            <StyledSectionTotalTableCell size="small" padding="normal">
                              {index + 1}
                            </StyledSectionTotalTableCell>
                            <StyledSectionTotalTableCell size="small" padding="normal">
                              {log.teamName}
                            </StyledSectionTotalTableCell>
                            <StyledSectionTotalTableCell size="small" align={'center'}>
                              {watchResetPoints
                                ? '0.00'
                                : watchDividePointsBy
                                  ? (log.points / watchDividePointsBy).toFixed(2)
                                  : '0.00'}
                            </StyledSectionTotalTableCell>
                            <StyledSectionTotalTableCell size="small" padding="normal">
                              {watchKeepNetScores ? log.net : 0}
                            </StyledSectionTotalTableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ))
              )}
            </Box>
          )}

          <Typography>
            <Box mt={2} fontStyle="italic" color="text.secondary">
              {watchSplitMode === 'sections'
                ? `Note: The section will be split into ${watchNumSections} equal sections with roughly equal number of teams in each.`
                : `Note: The section will be split after position ${watchSplitAt}.`}{' '}
              The points will be{' '}
              {watchResetPoints
                ? 'reset to 0'
                : `divided by ${watchDividePointsBy || 'the specified value'}`}
              .{' '}
              <Typography component="span" color="error" fontWeight={'bold'}>
                {' '}
                This can not be undone. Please ensure you have the correct section selected before
                proceeding.
              </Typography>
            </Box>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => onClose()}
            color="primary"
            disabled={splittingSection}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSubmit(handleSplit)()}
            color="success"
            disabled={splittingSection}>
            {splittingSection ? 'Splitting...' : 'Split'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SplitSectionDialog;
